import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import {
  catchError,
  filter,
  ignoreElements,
  mergeMap,
  pluck,
  withLatestFrom,
} from 'rxjs/operators';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { appError } from '@bridebook/web/lib/app/actions';
import { EnquiriesActionTypes } from '@bridebook/web/lib/enquiries/action-types';
import { getEnquiryMessageVariant } from '@bridebook/web/lib/enquiries/hooks/use-enquiry-message-variant';
import { getCurrentUserId } from '@bridebook/web/lib/users/selectors';
import {
  getEnquiryConfirmationScreenShown,
  getOnPageEnquiry,
} from '@bridebook/web/lib/enquiries/selectors';
import { plannerTriggeredFormCta } from '@bridebook/web/lib/enquiries/planner-enquiry';
import { getDefaultEnquiryMessage } from '@bridebook/web/lib/enquiries/utils/context-mapping';
import { isSupplierVenue } from '@bridebook/web/lib/supplier/utils';
import { ActionWithPayload, IEnquiryFormToggleArgs, IEpic } from '@bridebook/web/lib/types';
import enquiriesServerSideTrack from '../../utils/enquiries-server-side-track';

type InputActions = ActionWithPayload<IEnquiryFormToggleArgs>;

const enquiryFormToggleAnalyticsEpic: IEpic<InputActions> = (action$, { state$ }) =>
  action$.pipe(
    ofType(EnquiriesActionTypes.ENQUIRY_FORM_TOGGLE),
    pluck<ActionWithPayload<IEnquiryFormToggleArgs>, IEnquiryFormToggleArgs>('payload'),
    filter(({ track, supplier }) => !!track && !!supplier),
    withLatestFrom(state$),
    mergeMap(([payload, state]) => {
      const { show, infoProps, supplier } = payload;
      const contactedSupplier = supplier as IUISupplier;
      const onPageEnquiry = getOnPageEnquiry(state);
      const enquiryConfirmationScreenShown = getEnquiryConfirmationScreenShown(state);

      const confirmationScreen = enquiryConfirmationScreenShown && !onPageEnquiry;

      const event = show
        ? 'Triggered enquiry form'
        : confirmationScreen
          ? 'Closed confirmation screen'
          : 'Closed enquiry form';

      const message = show
        ? getDefaultEnquiryMessage(
            isSupplierVenue(contactedSupplier),
            (infoProps?.context ?? infoProps?.method ?? '') as string,
            getEnquiryMessageVariant(getCurrentUserId(state) ?? ''),
            infoProps?.fair,
          )
        : undefined;

      // A form the planner opened names the card button that opened it.
      const plannerCta = show ? plannerTriggeredFormCta(infoProps) : undefined;

      const promise = enquiriesServerSideTrack({
        state,
        contactedSupplier,
        event,
        infoProps: plannerCta ? { ...infoProps, cta: plannerCta } : infoProps,
        message,
      });

      return from(promise).pipe(
        ignoreElements(),
        catchError((error: Error) =>
          of(appError({ error, feature: 'enquiryFormToggleAnalyticsEpic' })),
        ),
      );
    }),
  );

export default enquiryFormToggleAnalyticsEpic;
